import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaHeadset,
  FaUserCheck,
  FaComments
} from 'react-icons/fa';

const UseCasesSection = styled.section`
  padding: 5rem 2rem;
  background: #f8f9fa;
  
  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #0891b2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #6b7280;
  max-width: 700px;
  margin: 0 auto;
  font-weight: 500;
`;

const UseCasesTabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 2rem;
  }
`;

const TabButton = styled(motion.button)`
  background: ${props => props.active ? 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)' : '#ffffff'};
  color: ${props => props.active ? 'white' : '#1a1a1a'};
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.active ? 'transparent' : '#e5e7eb'};
  box-shadow: ${props => props.active ? '0 4px 15px rgba(8, 145, 178, 0.2)' : '0 2px 10px rgba(0, 0, 0, 0.05)'};
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
  
  &:hover {
    background: ${props => props.active ? 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)' : '#f5f5f5'};
    border-color: ${props => props.active ? 'transparent' : '#0891b2'};
  }
`;

const UseCaseContent = styled(motion.div)`
  background: #ffffff;
  border: 2px solid #f0f0f0;
  border-radius: 20px;
  padding: 3rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 2rem 1.5rem;
    gap: 2rem;
  }
`;

const ContentLeft = styled.div``;

const UseCaseIcon = styled.div`
  font-size: 3rem;
  color: #0891b2;
  margin-bottom: 1rem;
`;

const UseCaseTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
  font-weight: 700;
`;

const UseCaseDescription = styled.p`
  color: #6b7280;
  line-height: 1.8;
  margin-bottom: 2rem;
  font-weight: 500;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
`;

const BenefitItem = styled.li`
  color: #6b7280;
  margin-bottom: 1rem;
  padding-left: 1.5rem;
  position: relative;
  font-weight: 400;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #4ade80;
    font-weight: bold;
  }
`;

const ConversationDemo = styled.div`
  background: #f8f9fa;
  border: 2px solid #f0f0f0;
  border-radius: 20px;
  padding: 2rem;
  max-height: 400px;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
    max-height: 300px;
  }
`;

const Message = styled(motion.div)`
  margin-bottom: 1rem;
  display: flex;
  justify-content: ${props => props.isUser ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div`
  background: ${props => props.isUser ? 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)' : '#e5e7eb'};
  color: ${props => props.isUser ? 'white' : '#1a1a1a'};
  padding: 0.75rem 1.25rem;
  border-radius: 20px;
  max-width: 80%;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 0.625rem 1rem;
    max-width: 85%;
  }
`;

const useCases = [
  {
    id: 'acquisition',
    icon: <FaHeadset />,
    title: 'Player Acquisition & Onboarding',
    description: 'Convert visitors into active players with intelligent onboarding that guides them from registration to first deposit.',
    benefits: [
      'Personalized welcome journeys',
      'Smart bonus recommendations',
      'Registration assistance',
      'First deposit optimization'
    ],
    conversation: [
      { isUser: true, text: "I'm new here, what games should I try?" },
      { isUser: false, text: "Welcome! Based on your preferences, I recommend starting with our popular slots. New players get 100 free spins on Starburst plus a 100% match bonus up to €500." },
      { isUser: true, text: "Sounds good! How do I claim it?" },
      { isUser: false, text: "I'll guide you through it! First, complete your registration. Then make a minimum deposit of €20, and the bonus will be automatically credited. Would you like me to walk you through the process?" }
    ]
  },
  {
    id: 'engagement',
    icon: <FaUserCheck />,
    title: 'Lifecycle Engagement & Retention',
    description: 'Keep players active and engaged with personalized campaigns triggered by behavior patterns and preferences.',
    benefits: [
      'Behavioral trigger campaigns',
      'Personalized game recommendations',
      'VIP journey progression',
      'Win-back automation'
    ],
    conversation: [
      { isUser: true, text: "I haven't won anything in a while" },
      { isUser: false, text: "I understand that can be frustrating. I've noticed you enjoy slot games. We have a special reload bonus just for you - 50% up to €100 on your next deposit, plus 25 free spins on your favorite game, Book of Dead." },
      { isUser: true, text: "That's exactly the game I like!" },
      { isUser: false, text: "Perfect! This exclusive offer is available for the next 24 hours. You're also just €50 away from reaching Silver VIP status, which includes weekly cashback. Shall I activate the bonus for you?" }
    ]
  },
  {
    id: 'support',
    icon: <FaComments />,
    title: 'Intelligent Support & Problem Resolution',
    description: 'Resolve player issues instantly while identifying opportunities to enhance their experience and value.',
    benefits: [
      'Instant issue resolution',
      'Proactive problem prevention',
      'Upsell opportunity detection',
      'Satisfaction monitoring'
    ],
    conversation: [
      { isUser: true, text: "My withdrawal is taking longer than expected" },
      { isUser: false, text: "I'll check that for you right away. Your €500 withdrawal is in the final verification stage and will be processed within 2 hours. As a valued player, you qualify for our VIP program with instant withdrawals. Would you like to learn more?" },
      { isUser: true, text: "Instant withdrawals sound great!" },
      { isUser: false, text: "Excellent! With Gold VIP status, you'll get instant withdrawals, 10% weekly cashback, and a dedicated account manager. You're already 70% of the way there. I can show you how to reach Gold status this week." }
    ]
  }
];

const UseCases = () => {
  const [activeUseCase, setActiveUseCase] = useState(useCases[0]);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <UseCasesSection id="use-cases">
      <Container ref={ref}>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            See Faroflow in Action
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Real conversations that happen thousands of times daily in online casinos
          </SectionSubtitle>
        </SectionHeader>

        <UseCasesTabs>
          {useCases.map((useCase) => (
            <TabButton
              key={useCase.id}
              active={activeUseCase.id === useCase.id}
              onClick={() => setActiveUseCase(useCase)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {useCase.title}
            </TabButton>
          ))}
        </UseCasesTabs>

        <AnimatePresence mode="wait">
          <UseCaseContent
            key={activeUseCase.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <ContentLeft>
              <UseCaseIcon>{activeUseCase.icon}</UseCaseIcon>
              <UseCaseTitle>{activeUseCase.title}</UseCaseTitle>
              <UseCaseDescription>{activeUseCase.description}</UseCaseDescription>
              <BenefitsList>
                {activeUseCase.benefits.map((benefit, index) => (
                  <BenefitItem key={index}>{benefit}</BenefitItem>
                ))}
              </BenefitsList>
            </ContentLeft>
            
            <ConversationDemo>
              {activeUseCase.conversation.map((message, index) => (
                <Message
                  key={index}
                  isUser={message.isUser}
                  initial={{ opacity: 0, x: message.isUser ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                >
                  <MessageBubble isUser={message.isUser}>
                    {message.text}
                  </MessageBubble>
                </Message>
              ))}
            </ConversationDemo>
          </UseCaseContent>
        </AnimatePresence>
      </Container>
    </UseCasesSection>
  );
};

export default UseCases; 